import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  MapPin,
  AlertCircle,
  ChevronLeft,
  Home,
  Maximize2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { Button } from "@/components/ui/button";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { formatPhoneInput } from "@/lib/utils";
import { useSolicitacaoDetails } from "@/hooks/useSolicitacaoDetails";
import { useSolicitacaoUpdates } from "@/hooks/useSolicitacaoUpdates";
import { MapSolicitation } from "@/components/solicitacao/map-solicitation";
import {
  STATUS_COLORS,
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  STATUS_ORDER,
} from "@/lib/status-mapping";
import { SolicitacoSkeleton } from "@/components/solicitacao/skeleton";
import { ShareRideCard } from "@/components/solicitacao/share-ride-card";
import { showToast } from "@/hooks/useToast";
import { useEstimatedTime } from "@/hooks/use-estimated-time";
import type { SolicitacaoStatus } from "@/services/schemas/solicitacao.schema";

import successAnimation from "../../../../assets/animations/success.json"
import cancelAnimation from "../../../../assets/animations/canceled.json"

export const Route = createFileRoute("/app/entregas/$entregaId/")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({
        to: "/auth/identify",
      });
    }
  },
  component: RouteComponent,
});

import { extractOrderId } from "@/lib/utils";

function RouteComponent() {
  const { entregaId: rawId } = Route.useParams();
  const navigate = useNavigate();
  const entregaId = Number(extractOrderId(rawId));

  useEffect(() => {
    if (!entregaId || isNaN(entregaId)) {
      navigate({ to: "/app/entregas" });
    }
  }, [entregaId, navigate]);

  const {
    solicitacao,
    isLoading,
    error,
    isCanceling,
    showCancelDialog,
    handleConfirmCancel,
    handleCancelDialog,
    handleGoBack,
  } = useSolicitacaoDetails(entregaId);

  const prevStatusRef = useRef<SolicitacaoStatus | null>(null);
  const [statusAnimKey, setStatusAnimKey] = useState(0);
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);
  const completionShownRef = useRef(false);

  useEffect(() => {
    if (!solicitacao?.status) return;
    const isFirstLoad = prevStatusRef.current === null;
    const statusChanged = !isFirstLoad && prevStatusRef.current !== solicitacao.status;

    if (statusChanged) {
      setStatusAnimKey((k) => k + 1);

      const isNowComplete =
        solicitacao.status === "delivered" ||
        solicitacao.status === "canceled" ||
        solicitacao.status === "rejected";

      if (isNowComplete && !completionShownRef.current) {
        completionShownRef.current = true;
        setShowCompletionOverlay(true);
      }
    }

    prevStatusRef.current = solicitacao.status;
  }, [solicitacao?.status]);

  const handleStatusChange = (newStatus: SolicitacaoStatus, _?: SolicitacaoStatus) => {
    const newStatusLabel = STATUS_LABELS[newStatus];
    showToast.success(`Status atualizado para: ${newStatusLabel}`);
  };

  useSolicitacaoUpdates(solicitacao?.updateChannel, entregaId, undefined, handleStatusChange);

  const statusColor = solicitacao ? STATUS_COLORS[solicitacao.status] : STATUS_COLORS['analysis'];

  const origin = solicitacao
    ? { lat: +solicitacao.origemSnapshot.latitude, lng: +solicitacao.origemSnapshot.longitude }
    : { lat: 0, lng: 0 };
  const destination = solicitacao
    ? { lat: +solicitacao.destinoSnapshot.latitude, lng: +solicitacao.destinoSnapshot.longitude }
    : { lat: 0, lng: 0 };
  const { distance } = useEstimatedTime({ origin, destination, status: solicitacao?.status });

  if (isLoading) {
    return <SolicitacoSkeleton />;
  }

  if (error || !solicitacao) {
    return (
      <div className="min-h-screen  flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-6 border border-black/5 text-center max-w-sm">
          <div className="bg-rose-100 rounded-full p-3 inline-block mb-4">
            <AlertCircle className="h-6 w-6 text-rose-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Ops!</h2>
          <p className="text-sm text-slate-600 mb-4">
            {error || "Algo deu errado"}
          </p>
          <Button onClick={handleGoBack} className="w-full rounded-xl h-11">
            Voltar
          </Button>
        </div>
      </div>
    );
  }


  const isCanceled =
    solicitacao.status === "canceled" ||
    solicitacao.status === "rejected";

  const isFlowComplete = isCanceled || solicitacao.status === "delivered";

  let currentStatusIndex = STATUS_ORDER.indexOf(solicitacao.status);
  if (isCanceled) {
    currentStatusIndex = STATUS_ORDER.indexOf("delivered");
  }

  const isDelivered = solicitacao.status === "delivered";

  return (
    <>
      <div className="min-h-screen">
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
          <div className="container mx-auto max-w-2xl px-4 py-3">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleGoBack}
                className="rounded-full hover:bg-black/5 -ml-2"
              >
                <ChevronLeft className="h-5 w-5 text-primary" />
              </Button>
              <h1 className="text-base font-semibold text-slate-900">
                Entrega #{solicitacao.externalId}
              </h1>
              <div className="w-10" />
            </div>
          </div>
        </header>

        <main className="container mx-auto max-w-2xl px-4 py-4 pb-24 space-y-3">
          <div className="bg-white rounded-2xl p-4 border border-black/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex flex-col gap-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={statusAnimKey}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="flex items-center gap-2"
                  >
                    <div
                      className={`h-2 w-2 rounded-full animate-pulse ${statusColor.dot}`}
                    />
                    <span className={`text-lg font-bold ${statusColor.text}`}>
                      {STATUS_LABELS[solicitacao.status]}
                    </span>
                  </motion.div>
                </AnimatePresence>
              </div>
              <span className="text-xs text-slate-400 self-start mt-0.5">
                {new Date(solicitacao.createdAt).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            {/* Progress bar */}
            <div className="flex items-center gap-1 mb-2">
              {STATUS_ORDER.map((status, index) => {
                const isActive = index <= currentStatusIndex;
                return (
                  <motion.div
                    key={status}
                    className={`h-1 flex-1 rounded-full ${isActive ? statusColor.dot : "bg-slate-200"}`}
                    initial={{ scaleX: 0, originX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.06, ease: "easeOut" }}
                  />
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={statusAnimKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="text-xs text-slate-500"
              >
                {STATUS_DESCRIPTIONS[solicitacao.status]}
              </motion.p>
            </AnimatePresence>

            <AnimatePresence>
              {isCanceled && (
                <motion.div
                  initial={{ opacity: 0, height: 0, marginTop: 0 }}
                  animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-rose-50 rounded-xl p-3 flex items-center gap-2 overflow-hidden"
                >
                  <AlertCircle className="h-4 w-4 text-rose-600 shrink-0" />
                  <p className="text-xs text-rose-700 font-medium">
                    Entrega cancelado
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {!isFlowComplete && (
              <motion.div
                key="map-section"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.97, height: 0, marginTop: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="rounded-2xl border h-48 border-black/5 overflow-hidden relative"
              >
                <MapSolicitation
                  originMarker={{
                    latitude: +solicitacao.origemSnapshot.latitude,
                    longitude: +solicitacao.origemSnapshot.longitude,
                    icon: <Home className="h-4 w-4 text-white" />,
                    label: "Origem",
                    color: "bg-green-500",
                  }}
                  destinationMarker={{
                    latitude: +solicitacao.destinoSnapshot.latitude,
                    longitude: +solicitacao.destinoSnapshot.longitude,
                    icon: <MapPin className="h-4 w-4 text-white" />,
                    label: "Destino",
                    color: "bg-red-500",
                  }}
                  deliveryChannel={solicitacao.deliveryChannel!}
                  status={solicitacao.status}
                />
                <Link
                  to={`/app/entregas/$entregaId/acompanhar`}
                  params={{ entregaId: String(solicitacao.id) }}
                  className="absolute z-10 bottom-2 right-2 flex items-center gap-2 bg-white px-3 py-2 rounded-full active:scale-95 transition-all cursor-pointer"
                >
                  <Maximize2 className="h-3.5 w-3.5 text-slate-900" />
                  <span className="text-xs font-semibold text-slate-900">
                    Acompanhar de pertinho
                  </span>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Share card - available for all statuses */}
          <AnimatePresence>
            <motion.div
              key="share-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12, scale: 0.97 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <ShareRideCard
                entregaId={solicitacao.externalId}
              />
            </motion.div>
          </AnimatePresence>

          {/* Rota / endereços */}
          <div className="bg-white rounded-2xl p-4 border border-black/5">
            <div className="space-y-4">
              {solicitacao.origemSnapshot && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="bg-primary/10 rounded-full p-1.5">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                    <div className="w-px h-full bg-slate-200 my-1" />
                  </div>
                  <div className="flex-1 pb-2">
                    <p className="text-xs text-slate-500 mb-1">Origem</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {solicitacao.origemSnapshot.nome}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {solicitacao.origemSnapshot.logradouro}
                      {solicitacao.origemSnapshot.numero &&
                        `, ${solicitacao.origemSnapshot.numero}`}
                    </p>
                    {solicitacao.origemSnapshot.bairro && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {solicitacao.origemSnapshot.bairro}
                      </p>
                    )}
                    {solicitacao.origemSnapshot.telefone && (
                      <a
                        href={`tel:${solicitacao.origemSnapshot.telefone}`}
                        className="text-xs text-primary font-medium mt-1 inline-block"
                      >
                        {formatPhoneInput(solicitacao.origemSnapshot.telefone)}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {solicitacao.destinoSnapshot && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="bg-emerald-100 rounded-full p-1.5">
                      <MapPin className="h-3 w-3 text-emerald-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500 mb-1">Destino</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {solicitacao.destinoSnapshot.nome}
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {solicitacao.destinoSnapshot.logradouro}
                      {solicitacao.destinoSnapshot.numero &&
                        `, ${solicitacao.destinoSnapshot.numero}`}
                    </p>
                    {solicitacao.destinoSnapshot.bairro && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {solicitacao.destinoSnapshot.bairro}
                      </p>
                    )}
                    {solicitacao.destinoSnapshot.telefone && (
                      <a
                        href={`tel:${solicitacao.destinoSnapshot.telefone}`}
                        className="text-xs text-primary font-medium mt-1 inline-block"
                      >
                        {formatPhoneInput(solicitacao.destinoSnapshot.telefone)}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pagamento */}
          <div className="bg-white rounded-2xl p-4 border border-black/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-slate-900">
                Pagamento
              </span>
              {solicitacao.formaPagamento && (
                <span className="text-xs font-medium text-slate-600 uppercase">
                  {solicitacao.formaPagamento}
                </span>
              )}
            </div>
            {solicitacao.valor_entrega && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Total</span>
                <span className="text-lg font-bold text-slate-900">
                  R$ {parseFloat(solicitacao.valor_entrega).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          {/* Detalhes gerais */}
          <div className="bg-white rounded-2xl p-4 border border-black/5">
            <div className="space-y-2.5">
              {solicitacao.tipo && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Tipo</span>
                  <span className="text-xs font-medium text-slate-700">
                    {solicitacao.tipo === "send" ? "Envio" : "Recebimento"}
                  </span>
                </div>
              )}
              {distance && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Distância</span>
                  <span className="text-xs font-medium text-slate-700">
                    {distance}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">Data</span>
                <span className="text-xs font-medium text-slate-700">
                  {new Date(solicitacao.createdAt).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>

          {/*{canCancel && (*/}
          {/*  <Button*/}
          {/*    variant="outline"*/}
          {/*    onClick={handleCancelRequest}*/}
          {/*    disabled={isCanceling}*/}
          {/*    className="w-full rounded-xl h-12 border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300"*/}
          {/*  >*/}
          {/*    {isCanceling ? "Cancelando..." : "Cancelar Entrega"}*/}
          {/*  </Button>*/}
          {/*)}*/}
        </main>
      </div>

      <AnimatePresence>
        {showCancelDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="bg-white rounded-3xl p-6 max-w-sm w-full"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Cancelar Entrega?
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Tem certeza que deseja cancelar esta entrega? Esta ação não
                pode ser desfeita.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleCancelDialog}
                  className="flex-1 rounded-xl"
                  disabled={isCanceling}
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleConfirmCancel}
                  className="flex-1 rounded-xl bg-rose-600 hover:bg-rose-700"
                  disabled={isCanceling}
                >
                  {isCanceling ? "Cancelando..." : "Confirmar"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCompletionOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-white/90 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
              className="flex flex-col items-center gap-4"
            >
              <Lottie
                animationData={isDelivered ? successAnimation : cancelAnimation}
                loop={false}
                autoplay={true}
                style={{ width: 200, height: 200 }}
                onComplete={() => {
                  setTimeout(() => setShowCompletionOverlay(false), 400);
                }}
              />
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className={`text-xl font-bold ${isDelivered ? "text-emerald-600" : "text-rose-600"}`}
              >
                {isDelivered ? "Entrega realizada!" : "Entrega cancelada"}
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
